import { afterRenderEffect, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { SwUpdate, UnrecoverableStateEvent, VersionEvent } from '@angular/service-worker';
import { BUILD_TIME } from '@core/environments/build-info';
import { environment } from '@core/environments/environment';
import { TranslationBootService } from '@core/services/translation-boot.service';
import { WindowRedirectService } from '@core/services/window-redirect.service';
import { getEarlyAppVersionCheck, IEarlyAppVersion } from '@core/utils/early-app-version-check';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { ConfirmDialogComponent, IConfirmDialogData } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { interval } from 'rxjs';

interface IVersionFile {
  buildTime?: string;
  version: string;
}

type TPendingUpdateSource = 'service-worker' | 'version-file' | 'unrecoverable' | null;

const APP_UPDATE_BYPASS_PARAM = 'ngsw-bypass';
const APP_UPDATE_CACHE_BUSTER_PARAM = 'app-update';
const VERSION_FILE_REFRESH_ATTEMPT_STORAGE_KEY = 'app-update:version-file-refresh-attempt';
const AUTO_REFRESH_ON_BOOT_STORAGE_KEY = 'app-update:auto-refresh-on-boot-attempted';
// If an update is detected before AppComponent has rendered real app content, there's nothing
// for the user to lose by refreshing, so we do it silently instead of showing a dialog. The
// afterRenderEffect below marks the first render where TranslationBootService has made content
// available.
//
// Two checks can detect an update this early in boot: the version-file check
// (checkEarlyAppVersion() and its immediate checkForVersionMismatch(true) fallback), and a native
// service-worker VERSION_READY event (listenForUpdates()). For a returning visitor with an
// already-active service worker, registerWhenStable doesn't gate the boot-time checkForUpdate()
// call, so that native event can fire well under a second into boot - in practice it usually wins
// the race against both the version-file check and the first render, meaning without this
// exception the silent-refresh path would rarely engage for returning visitors at all, which are
// most real users. Either check usually resolves before that first render, but on a slow
// connection - or, for the native event, whenever it simply wins that race - it can lose it. A
// user genuinely cannot have started anything worth protecting in the first few seconds of a cold
// boot, so either one is still allowed to refresh silently even if render narrowly won - but only
// within BOOT_AUTO_REFRESH_WINDOW_MS of construction, and only for that boot-originated detection
// (see isPendingUpdateFromBootCheck below). The periodic recheck (checkForVersionMismatch() from
// scheduleUpdateChecks() - isBootTimeCheck defaults to false there) and any update detected after
// that window has elapsed always prompt once render has happened, with no exception: by then the
// user may well be mid-task.
//
// Bounds how long after construction either boot-time check above can still refresh silently even
// if render already happened. Generous enough to cover a slow connection without straying
// anywhere near "the user could plausibly be mid-task" territory.
const BOOT_AUTO_REFRESH_WINDOW_MS = 5000;

// Matches the Cache Storage name the "images" dataGroup in ngsw-config.json ends up with at
// runtime (ngsw-worker.js's DataGroup names its cache "<scope-prefix>:<dataGroup.version>:data:
// <dataGroup.name>:cache" - see NamedCacheStorage/DataGroup in @angular/service-worker). The
// service worker caches that dataGroup purely by request URL; it has no concept of which user is
// currently signed in, so a response cached while one user was signed in would otherwise still be
// served - without ever re-checking authorization - to whichever user is signed in next on the
// same browser profile. Clearing it on sign-out (see AuthService.signOut) closes that window for
// the common case (a shared/handed-off device); it doesn't cover a browser crash or force-closed
// tab that skips the sign-out flow entirely, which is part of why ngsw-config.json also keeps
// maxAge short rather than relying on this alone.
const IMAGE_DATA_GROUP_CACHE_SUFFIX = ':data:images:cache';
// Bounds how long clearImageDataGroupCache() can block a caller that awaits it (see
// AuthService.signOut(), the only caller that does). Cache Storage/IndexedDB calls hanging
// indefinitely is a real, known failure mode in this app - it's the whole reason
// image-utilities.service.ts adds its own client-side fetch timeout as a backstop against the
// service worker's cache write hanging. Without a bound here, that same class of hang would now
// also hang signOut() itself (and anything a caller chains after it, e.g.
// accept-creator-invitation.component.ts's handleSignIn() chaining straight into a sign-in for a
// different user) indefinitely, which is a strictly worse outcome than proceeding with a cache
// that might not have finished clearing.
const CLEAR_IMAGE_CACHE_TIMEOUT_MS = 3000;
// SwUpdate.unrecoverable fires when the service worker itself determines its own state can't be
// trusted anymore (e.g. the active manifest references a cached resource the browser has since
// evicted) - Angular's own authoritative signal for a genuinely broken service worker, unlike the
// empty-cache heuristic this file used to hand-roll for a similar purpose (removed: it couldn't
// tell a real corruption apart from ordinary Cache Storage quota eviction, e.g. in Incognito).
// This does NOT mean anything the user has done in-memory since is lost or worthless, so
// listenForUnrecoverableState() below routes it through the same promptForRefresh()/
// canAutoRefreshSilently() machinery every other update source uses rather than forcing an
// unconditional reload - pre-render there's nothing to lose yet, so it's eligible for the same
// silent-refresh path as the other sources (capped to one attempt per tab by the shared
// AUTO_REFRESH_ON_BOOT_STORAGE_KEY guard, same as them); post-render,
// isEligibleForPostRenderBootRefresh() deliberately excludes 'unrecoverable' from its source list,
// so it always falls through to the normal confirm dialog instead of silently discarding whatever
// the user may be mid-task on.

@Injectable({
  providedIn: 'root',
})
export class AppUpdateService {
  public readonly isReloading = signal<boolean>(false);

  // performance.now(), not Date.now(): the latter is wall-clock time, so a backwards system-clock
  // adjustment (NTP correction, manual change, a laptop waking from sleep) after construction
  // could otherwise make isEligibleForPostRenderBootRefresh()'s elapsed-time check go negative
  // and silently reopen the boot-refresh exception well into a real session. performance.now() is
  // monotonic and scoped to this page load, so it can't be affected by wall-clock adjustments.
  private readonly bootTimestamp = performance.now();
  private readonly checkIntervalMs = 180000;
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly swUpdate = inject(SwUpdate);
  private readonly translate = inject(TranslateService);
  private readonly translationBoot = inject(TranslationBootService);

  private hasUpdateAvailable = false;
  private hasPromptedForPendingUpdate = false;
  private isUpdateDialogOpen = false;
  private hasRenderedAppContent = false;
  private isPendingUpdateFromBootCheck = false;
  // Separate from pendingUpdateSource on purpose: an unrecoverable-state occurrence can be
  // reported before pendingUpdateSource ever becomes 'unrecoverable' (e.g. a different source's
  // dialog is already pending, so listenForUnrecoverableState() bails at its cross-source guard
  // without touching pendingUpdateSource at all) - deriving dedup from pendingUpdateSource alone
  // would miss exactly that case. Cleared in clearPendingUpdate() and when this source's own
  // dialog is declined (see promptForRefresh()), i.e. whenever the occurrence this flag was
  // tracking has actually concluded, so a later, genuinely new occurrence still gets reported.
  private hasReportedActiveUnrecoverableState = false;
  private pendingUpdateSource: TPendingUpdateSource = null;
  private pendingVersion: string | null = null;

  constructor() {
    afterRenderEffect({
      read: () => {
        if (this.translationBoot.ready() && !this.hasRenderedAppContent) {
          this.hasRenderedAppContent = true;
        }
      },
    });
  }

  // Called from AuthService.signOut() (which awaits it) and from several other session-end paths
  // that fire it without awaiting (interceptor forced-logout branches, the auth guard, a failed
  // post-auth navigation) - see each of those call sites for why. A failure here (e.g. Cache
  // Storage unavailable) is caught and logged rather than thrown, so it can never itself stop a
  // caller from proceeding with sign-out. Bounded by CLEAR_IMAGE_CACHE_TIMEOUT_MS (see above) so
  // that awaiting caller can't be blocked indefinitely if Cache Storage hangs - this method always
  // settles within that bound even if the underlying caches.keys()/caches.delete() calls never do
  // (the Cache Storage API has no way to actually cancel them; a hang past the timeout just means
  // the cache may not have actually finished clearing by the time this resolves, which is a
  // narrow, rare, and strictly better outcome than blocking the caller forever).
  public async clearImageDataGroupCache(): Promise<void> {
    await Promise.race([this.clearImageDataGroupCacheUnbounded(), this.delay(CLEAR_IMAGE_CACHE_TIMEOUT_MS)]);
  }

  private async clearImageDataGroupCacheUnbounded(): Promise<void> {
    try {
      const cacheKeys = await caches.keys();
      const imageCacheKeys = cacheKeys.filter(
        (key) => key.startsWith('ngsw:') && key.endsWith(IMAGE_DATA_GROUP_CACHE_SUFFIX),
      );

      await Promise.all(imageCacheKeys.map((key) => caches.delete(key)));
    } catch (error: unknown) {
      this.logError('Failed to clear image cache on sign-out.', error);
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  public init(): void {
    this.cleanupRefreshUrl();

    if (!this.swUpdate.isEnabled || window.location.hostname.includes('localhost')) {
      return;
    }

    this.listenForUpdates();
    this.listenForUnrecoverableState();
    this.listenForDialogClose();
    this.scheduleUpdateChecks();
    void this.checkEarlyAppVersion();
  }

  // Consumes the non-blocking version probe kicked off in main.ts before bootstrapApplication().
  // Gated behind the same flag as the periodic check (shouldUseVersionFileFallback) so that flag
  // stays the single kill switch for all version-file-based detection, early or periodic.
  private async checkEarlyAppVersion(): Promise<void> {
    if (!this.shouldUseVersionFileFallback()) {
      return;
    }

    try {
      const earlyAppVersion: IEarlyAppVersion | null = await getEarlyAppVersionCheck();

      if (!earlyAppVersion) {
        void this.checkForVersionMismatch(true);

        return;
      }

      if (!this.hasVersionFileMismatch(earlyAppVersion) || this.hasUpdateAvailable) {
        return;
      }

      const versionFileUpdateKey: string = this.getVersionFileUpdateKey(earlyAppVersion);

      // Same give-up rule checkForVersionMismatch() already applies below: don't re-prompt for a
      // version we already tried refreshing to this session (e.g. a slow-to-propagate deploy) -
      // without this, a fresh tab would re-open the dialog every time instead of backing off.
      this.clearVersionFileRefreshAttemptIfDifferent(versionFileUpdateKey);

      if (this.hasAttemptedVersionFileRefresh(versionFileUpdateKey)) {
        return;
      }

      this.hasUpdateAvailable = true;
      this.hasPromptedForPendingUpdate = false;
      this.pendingUpdateSource = 'version-file';
      this.pendingVersion = versionFileUpdateKey;
      this.isPendingUpdateFromBootCheck = true;
      this.promptForRefresh();
    } catch (error: unknown) {
      this.logError('Early app-version check failed.', error);
    }
  }

  private listenForUpdates(): void {
    this.swUpdate.versionUpdates.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: VersionEvent): void => {
      if (event.type === 'VERSION_READY') {
        if (!this.hasUpdateAvailable) {
          this.hasUpdateAvailable = true;
          this.hasPromptedForPendingUpdate = false;
          this.pendingUpdateSource = 'service-worker';
          this.pendingVersion = null;
          // A native VERSION_READY firing this early is functionally the same "nothing to lose
          // yet" situation the version-file boot check is allowed to auto-refresh through (see
          // isEligibleForPostRenderBootRefresh() and the BOOT_AUTO_REFRESH_WINDOW_MS comment
          // above) - and for a returning visitor it's usually THIS event, not the version-file
          // check, that wins the boot-time detection race. A VERSION_READY firing later in the
          // session (outside the window) does not get tagged as boot-time here -
          // isEligibleForPostRenderBootRefresh()'s own window check would reject it anyway, but
          // computing it here keeps this flag's meaning ("this pending update originated from a
          // boot-time check") accurate rather than incidentally true.
          this.isPendingUpdateFromBootCheck = performance.now() - this.bootTimestamp <= BOOT_AUTO_REFRESH_WINDOW_MS;
        }

        this.hasUpdateAvailable = true;
        this.promptForRefresh();

        return;
      }

      if (event.type === 'VERSION_INSTALLATION_FAILED') {
        this.logError('Service worker update installation failed.', event);
      }
    });
  }

  // See the SwUpdate.unrecoverable comment above CLEAR_IMAGE_CACHE_TIMEOUT_MS for why this goes
  // through the standard hasUpdateAvailable/pendingUpdateSource/promptForRefresh() state machine
  // instead of forcing an unconditional reload.
  private listenForUnrecoverableState(): void {
    this.swUpdate.unrecoverable
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event: UnrecoverableStateEvent) => {
        // Tracked independently of pendingUpdateSource (see hasReportedActiveUnrecoverableState's
        // own comment for why) so this dedup still applies even while a different source's dialog
        // is open and the cross-source guard below bails before pendingUpdateSource is ever set to
        // 'unrecoverable' - without an independent flag, that branch had no dedup at all, and a
        // worker re-emitting UNRECOVERABLE_STATE while some other update was pending would flood
        // Sentry with a report per event.
        if (!this.hasReportedActiveUnrecoverableState) {
          this.hasReportedActiveUnrecoverableState = true;
          this.reportUnrecoverableState(event);
        }

        // Mirrors checkForVersionMismatch()'s boot-time guard: don't let this clobber an update
        // from a different source that's already pending (e.g. a dialog already open for it) -
        // refreshing for that other update will resolve this unrecoverable state too, since any
        // refresh re-fetches everything fresh.
        if (this.hasUpdateAvailable && this.pendingUpdateSource !== 'unrecoverable') {
          return;
        }

        if (this.pendingUpdateSource !== 'unrecoverable') {
          this.hasPromptedForPendingUpdate = false;
        }

        // Deliberately not set from isPendingUpdateFromBootCheck: isEligibleForPostRenderBootRefresh()
        // never includes 'unrecoverable' in its source whitelist below, so this flag's value can't
        // affect this source's behavior either way - leaving it false keeps its meaning ("this
        // pending update originated from a boot-time check") accurate for the other sources that do
        // rely on it.
        this.hasUpdateAvailable = true;
        this.pendingUpdateSource = 'unrecoverable';
        this.pendingVersion = null;
        this.promptForRefresh();
      });
  }

  private reportUnrecoverableState(event: UnrecoverableStateEvent): void {
    Sentry.captureMessage('Service worker entered an unrecoverable state.', {
      extra: {
        reason: event.reason,
        // pathname only, deliberately not the full URL: some routes (e.g. the password-reset
        // flow) carry a single-use credential in the query string (new-password.component.ts's
        // oobCode), and that must never end up in Sentry.
        url: window.location.pathname,
      },
      level: 'error',
    });
  }

  private scheduleUpdateChecks(): void {
    interval(this.checkIntervalMs)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((): void => {
        void this.checkForUpdate();

        if (this.shouldUseVersionFileFallback()) {
          void this.checkForVersionMismatch();
        }

        if (this.hasUpdateAvailable) {
          this.promptForRefresh();
        }
      });

    void this.checkForUpdate();
  }

  private async checkForUpdate(): Promise<void> {
    try {
      await this.swUpdate.checkForUpdate();
    } catch (error: unknown) {
      this.logError('Service worker update check failed.', error);
    }
  }

  // isBootTimeCheck is true only when this runs as checkEarlyAppVersion()'s own fallback (the
  // early probe came back empty, so this is still effectively part of the boot sequence, not a
  // periodic recheck) - see canAutoRefreshSilently() and the BOOT_AUTO_REFRESH_WINDOW_MS comment
  // above for why that distinction matters. scheduleUpdateChecks()'s periodic interval calls this
  // with no argument, so it always defaults to false there.
  private async checkForVersionMismatch(isBootTimeCheck = false): Promise<void> {
    if (!this.shouldUseVersionFileFallback()) {
      return;
    }

    try {
      const versionFile: IVersionFile | null = await this.loadVersionFile();

      if (!versionFile?.version) {
        return;
      }

      const hasVersionMismatch: boolean = this.hasVersionFileMismatch(versionFile);

      if (hasVersionMismatch) {
        const versionFileUpdateKey: string = this.getVersionFileUpdateKey(versionFile);

        this.clearVersionFileRefreshAttemptIfDifferent(versionFileUpdateKey);

        if (this.hasAttemptedVersionFileRefresh(versionFileUpdateKey)) {
          if (this.pendingUpdateSource === 'version-file' && this.pendingVersion === versionFileUpdateKey) {
            this.clearPendingUpdate();
          }

          return;
        }

        // The boot-time fallback runs concurrently with listenForUpdates() at boot, which can
        // already have a pending, possibly already-prompted-and-declined native update by the
        // time this resolves. Bail out here, before touching ANY state, rather than after -
        // reclassifying that pending update as this boot check's version-file update would
        // silently drop the user's answer to the other dialog. Only isBootTimeCheck is gated
        // this way: the periodic recheck must still be free to re-prompt when a version-file
        // update it already reported turns out to have moved on to a newer one.
        if (isBootTimeCheck && this.hasUpdateAvailable) {
          return;
        }

        if (this.pendingVersion !== versionFileUpdateKey || this.pendingUpdateSource !== 'version-file') {
          this.hasPromptedForPendingUpdate = false;
        }

        this.hasUpdateAvailable = true;
        this.pendingUpdateSource = 'version-file';
        this.pendingVersion = versionFileUpdateKey;
        this.isPendingUpdateFromBootCheck = isBootTimeCheck;
        this.promptForRefresh();

        return;
      }

      if (this.pendingUpdateSource === 'version-file') {
        this.clearPendingUpdate();
      }

      this.clearVersionFileRefreshAttempt();
    } catch (error: unknown) {
      this.logError('Version file check failed.', error);
    }
  }

  private logError(message: string, error: unknown): void {
    // eslint-disable-next-line no-console
    console.error(message, error);
  }

  private listenForDialogClose(): void {
    this.dialog.afterAllClosed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.isUpdateDialogOpen = false;
    });
  }

  private promptForRefresh(): void {
    if (this.isReloading() || this.isUpdateDialogOpen || !this.hasUpdateAvailable || this.hasPromptedForPendingUpdate) {
      return;
    }

    if (this.canAutoRefreshSilently() && this.recordAutoRefreshOnBootAttempt()) {
      this.autoRefreshSilently();

      return;
    }

    this.hasPromptedForPendingUpdate = true;
    this.isUpdateDialogOpen = true;

    // A different check can still overwrite this.pendingUpdateSource/this.pendingVersion
    // while this dialog is open and awaiting a click (e.g. a version-file mismatch detected
    // while a native service-worker dialog is still up) - snapshot what THIS dialog is for so
    // the confirm handler below acts on that, not on whatever the fields hold by the time the
    // user actually clicks.
    const promptedSource = this.pendingUpdateSource;
    const promptedVersion = this.pendingVersion;

    this.dialog
      .open(ConfirmDialogComponent, {
        data: this.getDialogData(),
        disableClose: true,
        width: '400px',
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((shouldRefresh: boolean | undefined) => {
        this.isUpdateDialogOpen = false;

        if (shouldRefresh !== true) {
          // scheduleUpdateChecks()'s periodic interval() re-invokes promptForRefresh() every
          // checkIntervalMs whenever hasUpdateAvailable is still true, specifically so a declined
          // update keeps reminding the user - but that only works if hasPromptedForPendingUpdate
          // is re-armed here. Without this reset, a single decline permanently blocks every future
          // prompt for this same pending update until a genuinely different update source/version
          // comes along and resets it independently (see checkForVersionMismatch()).
          this.hasPromptedForPendingUpdate = false;

          if (promptedSource === 'unrecoverable') {
            // This occurrence has been shown to the user and declined - a later, genuinely new
            // unrecoverable event should be reported again (see hasReportedActiveUnrecoverableState).
            this.hasReportedActiveUnrecoverableState = false;
          }

          return;
        }

        if (promptedSource === 'version-file' && promptedVersion) {
          this.recordVersionFileRefreshAttempt(promptedVersion);
        }

        this.isReloading.set(true);
        this.refreshToLatestVersion();
      });
  }

  private async loadVersionFile(): Promise<IVersionFile | null> {
    const versionUrl: string = this.getVersionFileUrl();
    const response: Response = await fetch(versionUrl, {
      cache: 'no-store',
      headers: {
        [APP_UPDATE_BYPASS_PARAM]: 'true',
      },
    });

    if (!response.ok) {
      return null;
    }

    const contentType: string = response.headers.get('content-type') ?? '';
    const responseText: string = await response.text();

    if (!contentType.includes('application/json')) {
      return null;
    }

    if (responseText.trim().startsWith('<')) {
      return null;
    }

    const parsed: unknown = JSON.parse(responseText);

    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }

    const candidate = parsed as { buildTime?: unknown; version?: unknown };

    if (
      typeof candidate.version !== 'string' ||
      candidate.version.length === 0 ||
      (candidate.buildTime !== undefined && typeof candidate.buildTime !== 'string')
    ) {
      return null;
    }

    return {
      buildTime: candidate.buildTime,
      version: candidate.version,
    };
  }

  private getVersionFileUrl(): string {
    const basePath: string = WindowRedirectService.getRootPath();
    const normalizedBasePath: string = basePath.endsWith('/') ? basePath : `${basePath}/`;

    return new URL('version.json', `${window.location.origin}${normalizedBasePath}`).toString();
  }

  private refreshToLatestVersion(): void {
    this.reloadWithBypass();
  }

  private reloadWithBypass(): void {
    const refreshUrl = new URL(window.location.href);

    refreshUrl.searchParams.set(APP_UPDATE_BYPASS_PARAM, 'true');
    refreshUrl.searchParams.set(APP_UPDATE_CACHE_BUSTER_PARAM, Date.now().toString());
    this.replaceLocation(refreshUrl.toString());
  }

  private replaceLocation(url: string): void {
    window.location.replace(url);
  }

  private cleanupRefreshUrl(): void {
    const currentUrl = new URL(window.location.href);
    const hasBypassParam = currentUrl.searchParams.has(APP_UPDATE_BYPASS_PARAM);
    const hasCacheBusterParam = currentUrl.searchParams.has(APP_UPDATE_CACHE_BUSTER_PARAM);

    if (!hasBypassParam && !hasCacheBusterParam) {
      return;
    }

    currentUrl.searchParams.delete(APP_UPDATE_BYPASS_PARAM);
    currentUrl.searchParams.delete(APP_UPDATE_CACHE_BUSTER_PARAM);
    window.history.replaceState(
      window.history.state,
      '',
      `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`,
    );
  }

  private clearPendingUpdate(): void {
    this.hasUpdateAvailable = false;
    this.hasPromptedForPendingUpdate = false;
    this.pendingUpdateSource = null;
    this.pendingVersion = null;
    this.isPendingUpdateFromBootCheck = false;
    // The pending state this occurrence was blocked behind (see hasReportedActiveUnrecoverableState's
    // own comment) has now fully resolved, so a still-recurring unrecoverable state is a fresh
    // occurrence worth reporting again.
    this.hasReportedActiveUnrecoverableState = false;
  }

  private shouldUseVersionFileFallback(): boolean {
    return environment.useVersionFileAppUpdateFallback;
  }

  private hasVersionFileMismatch(versionFile: IVersionFile | IEarlyAppVersion): boolean {
    return (
      versionFile.version !== environment.version || (!!versionFile.buildTime && versionFile.buildTime !== BUILD_TIME)
    );
  }

  private getVersionFileUpdateKey(versionFile: IVersionFile | IEarlyAppVersion): string {
    return versionFile.version !== environment.version
      ? versionFile.version
      : versionFile.buildTime || versionFile.version;
  }

  private hasAttemptedVersionFileRefresh(version: string): boolean {
    try {
      return window.sessionStorage.getItem(VERSION_FILE_REFRESH_ATTEMPT_STORAGE_KEY) === version;
    } catch {
      return false;
    }
  }

  private recordVersionFileRefreshAttempt(version: string): void {
    try {
      window.sessionStorage.setItem(VERSION_FILE_REFRESH_ATTEMPT_STORAGE_KEY, version);
    } catch (error: unknown) {
      this.logError('Failed to record version-file refresh attempt.', error);
    }
  }

  private clearVersionFileRefreshAttempt(): void {
    try {
      window.sessionStorage.removeItem(VERSION_FILE_REFRESH_ATTEMPT_STORAGE_KEY);
    } catch {
      // Ignore storage failures; a healthy response should not fail the update check.
    }
  }

  private clearVersionFileRefreshAttemptIfDifferent(version: string): void {
    let attemptedVersion: string | null;

    try {
      attemptedVersion = window.sessionStorage.getItem(VERSION_FILE_REFRESH_ATTEMPT_STORAGE_KEY);
    } catch {
      return;
    }

    if (attemptedVersion && attemptedVersion !== version) {
      this.clearVersionFileRefreshAttempt();
    }
  }

  private canAutoRefreshSilently(): boolean {
    if (this.hasRenderedAppContent && !this.isEligibleForPostRenderBootRefresh()) {
      return false;
    }

    if (this.pendingUpdateSource === 'version-file' && this.pendingVersion) {
      if (this.hasAttemptedVersionFileRefresh(this.pendingVersion)) {
        return false;
      }
    }

    return !this.hasAlreadyAutoRefreshedOnBoot();
  }

  // The one exception to "never refresh silently once content has rendered": a boot-time update -
  // either the version-file check or a native service-worker VERSION_READY event (see
  // isPendingUpdateFromBootCheck) - narrowly losing its race against the first render, and only
  // within BOOT_AUTO_REFRESH_WINDOW_MS of construction. Anything else - the periodic recheck, a
  // native service-worker event firing later in the session, or either check resolving later than
  // that window - falls through to the normal dialog.
  private isEligibleForPostRenderBootRefresh(): boolean {
    return (
      this.isPendingUpdateFromBootCheck &&
      (this.pendingUpdateSource === 'version-file' || this.pendingUpdateSource === 'service-worker') &&
      performance.now() - this.bootTimestamp <= BOOT_AUTO_REFRESH_WINDOW_MS
    );
  }

  private autoRefreshSilently(): void {
    this.hasPromptedForPendingUpdate = true;

    if (this.pendingUpdateSource === 'version-file' && this.pendingVersion) {
      this.recordVersionFileRefreshAttempt(this.pendingVersion);
    }

    this.isReloading.set(true);
    this.refreshToLatestVersion();
  }

  private hasAlreadyAutoRefreshedOnBoot(): boolean {
    try {
      return window.sessionStorage.getItem(AUTO_REFRESH_ON_BOOT_STORAGE_KEY) === 'true';
    } catch {
      // Without a readable guard, showing the prompt is safer than risking a refresh loop.
      return true;
    }
  }

  private recordAutoRefreshOnBootAttempt(): boolean {
    try {
      window.sessionStorage.setItem(AUTO_REFRESH_ON_BOOT_STORAGE_KEY, 'true');

      return true;
    } catch {
      return false;
    }
  }

  private getDialogData(): IConfirmDialogData {
    const cancelTextKey = 'shared.appUpdate.cancelText';
    const confirmTextKey = 'shared.appUpdate.confirmText';
    const messageKey = 'shared.appUpdate.message';
    const titleKey = 'shared.appUpdate.title';

    const cancelText = this.translate.instant(cancelTextKey);
    const confirmText = this.translate.instant(confirmTextKey);
    const message = this.translate.instant(messageKey);
    const title = this.translate.instant(titleKey);

    return {
      cancelText: cancelText === cancelTextKey ? 'Not now' : cancelText,
      confirmText: confirmText === confirmTextKey ? 'Refresh' : confirmText,
      message: message === messageKey ? 'A new version is available. Refresh to update?' : message,
      swapButtons: false,
      title: title === titleKey ? 'Update Available' : title,
    };
  }
}
